import type { ChatMessageType } from '@/store/types';

export const AIDA_CHAT_EXPORT_FORMAT = 'aida-chat';
export const AIDA_CHAT_EXPORT_VERSION = 1;
export const AIDA_IMPORT_TURN_DELIMITER = '\n\u001e\n';
/** Encoded `msg` budget so `GET .../get?msg=` stays under typical URI limits. */
export const AIDA_IMPORT_CONTEXT_MAX_ENCODED_LENGTH = 7000;

export class AidaChatImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AidaChatImportError';
  }
}

export interface AidaChatExportMessage {
  sender: 'user' | 'bot';
  message: string;
  timestamp: string;
  reasoning?: string;
  reasoningSteps?: string[];
  interrupted?: boolean;
}

export interface AidaChatExportV1 {
  format: typeof AIDA_CHAT_EXPORT_FORMAT;
  version: typeof AIDA_CHAT_EXPORT_VERSION;
  exportedAt: string;
  title?: string;
  messages: AidaChatExportMessage[];
}

const CONTEXT_HEADER =
  'The following is a previous conversation for context. Use it to continue. Do not answer the previous turns; answer only the current question.\n\n';
const OMITTED_NOTE = 'Earlier turns were omitted because the conversation is too long.\n\n';
const CURRENT_QUESTION_HEADER = '\n\nCurrent question:\n';

const toIsoTimestamp = (timestamp: Date | string): string => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return new Date().toISOString();
  return date.toISOString();
};

export function serializeAidaChat({
  messages,
  title,
  exportedAt = new Date(),
}: {
  messages: ChatMessageType[];
  title?: string;
  exportedAt?: Date;
}): AidaChatExportV1 {
  const trimmedTitle = title?.trim();
  return {
    format: AIDA_CHAT_EXPORT_FORMAT,
    version: AIDA_CHAT_EXPORT_VERSION,
    exportedAt: exportedAt.toISOString(),
    ...(trimmedTitle ? { title: trimmedTitle } : {}),
    messages: messages.map(msg => {
      const exported: AidaChatExportMessage = {
        sender: msg.sender,
        message: msg.message,
        timestamp: toIsoTimestamp(msg.timestamp),
      };
      if (msg.reasoning) exported.reasoning = msg.reasoning;
      if (msg.reasoningSteps?.length) exported.reasoningSteps = [...msg.reasoningSteps];
      if (msg.interrupted) exported.interrupted = true;
      return exported;
    }),
  };
}

export function stringifyAidaChatExport(payload: AidaChatExportV1): string {
  return `${JSON.stringify(payload, null, 2)}\n`;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseExportedMessage = (value: unknown, index: number): AidaChatExportMessage => {
  if (!isRecord(value)) {
    throw new AidaChatImportError('This file is not a valid AIDA chat export.');
  }
  if (value.sender !== 'user' && value.sender !== 'bot') {
    throw new AidaChatImportError(`This file is not a valid AIDA chat export (message ${index + 1}).`);
  }
  if (typeof value.message !== 'string') {
    throw new AidaChatImportError(`This file is not a valid AIDA chat export (message ${index + 1}).`);
  }

  const timestamp =
    typeof value.timestamp === 'string' && value.timestamp.trim()
      ? toIsoTimestamp(value.timestamp)
      : new Date().toISOString();

  const message: AidaChatExportMessage = {
    sender: value.sender,
    message: value.message,
    timestamp,
  };

  if (typeof value.reasoning === 'string' && value.reasoning) {
    message.reasoning = value.reasoning;
  }
  if (Array.isArray(value.reasoningSteps) && value.reasoningSteps.every(step => typeof step === 'string')) {
    message.reasoningSteps = value.reasoningSteps;
  }
  if (value.interrupted === true) {
    message.interrupted = true;
  }

  return message;
};

export function parseAidaChatExport(raw: unknown): AidaChatExportV1 {
  if (!isRecord(raw)) {
    throw new AidaChatImportError('This file is not a valid AIDA chat export.');
  }
  if (raw.format !== AIDA_CHAT_EXPORT_FORMAT) {
    throw new AidaChatImportError('This file is not a valid AIDA chat export.');
  }
  if (raw.version !== AIDA_CHAT_EXPORT_VERSION) {
    throw new AidaChatImportError('This export format is not supported.');
  }
  if (!Array.isArray(raw.messages) || raw.messages.length === 0) {
    throw new AidaChatImportError('This chat export has no messages.');
  }

  const title = typeof raw.title === 'string' && raw.title.trim() ? raw.title.trim() : undefined;
  const exportedAt =
    typeof raw.exportedAt === 'string' && raw.exportedAt.trim()
      ? toIsoTimestamp(raw.exportedAt)
      : new Date().toISOString();

  return {
    format: AIDA_CHAT_EXPORT_FORMAT,
    version: AIDA_CHAT_EXPORT_VERSION,
    exportedAt,
    ...(title ? { title } : {}),
    messages: raw.messages.map(parseExportedMessage),
  };
}

export function parseAidaChatExportJson(text: string): AidaChatExportV1 {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new AidaChatImportError('This file is not a valid AIDA chat export.');
  }
  return parseAidaChatExport(parsed);
}

export function toImportedMessages(exported: AidaChatExportV1, now = Date.now()): ChatMessageType[] {
  return exported.messages.map((msg, index) => {
    const imported: ChatMessageType = {
      id: `${msg.sender}-${now}-${index}`,
      sender: msg.sender,
      message: msg.message,
      timestamp: msg.timestamp,
    };
    if (msg.reasoning) imported.reasoning = msg.reasoning;
    if (msg.reasoningSteps?.length) imported.reasoningSteps = [...msg.reasoningSteps];
    if (msg.interrupted) imported.interrupted = true;
    return imported;
  });
}

export function buildPendingImportContext(messages: Array<{ sender: string; message: string }>): string {
  return messages
    .filter(msg => (msg.sender === 'user' || msg.sender === 'bot') && msg.message.trim())
    .map(msg => `${msg.sender === 'user' ? 'User' : 'AIDA'}: ${msg.message.trim()}`)
    .join(AIDA_IMPORT_TURN_DELIMITER);
}

const encodedLength = (value: string): number => encodeURIComponent(value).length;

export function composeImportedFollowUpMessage(
  pendingContext: string,
  userMessage: string,
  maxEncodedLength = AIDA_IMPORT_CONTEXT_MAX_ENCODED_LENGTH,
): string {
  const footer = `${CURRENT_QUESTION_HEADER}${userMessage}`;
  const questionOnly = `${CONTEXT_HEADER}${footer.trimStart()}`;
  if (encodedLength(questionOnly) > maxEncodedLength) {
    return userMessage;
  }

  const turns = pendingContext.split(AIDA_IMPORT_TURN_DELIMITER).filter(Boolean);
  if (turns.length === 0) return userMessage;

  let omitted = false;
  let used = [...turns];

  while (used.length > 0) {
    const body = `${omitted ? OMITTED_NOTE : ''}${used.join('\n\n')}`;
    const full = `${CONTEXT_HEADER}${body}${footer}`;
    if (encodedLength(full) <= maxEncodedLength) return full;
    used = used.slice(1);
    omitted = true;
  }

  return userMessage;
}

export function readImportedChatFile(file: File): Promise<string> {
  if (typeof file.text === 'function') {
    return file.text();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsText(file);
  });
}
